import _ from 'underscore';

import { KioskHelper } from '@biteinc/business-rules';
import {
  KioskDeviceTypeHelper,
  KioskPaymentTerminalModelHelper,
  MdmProviderHelper,
  PrinterTypeHelper,
} from '@biteinc/enums';
import { Time } from '@biteinc/helpers';

import type { AbstractModel } from './types/abstract_model';
import type { KioskData } from './types/kiosk_data';
import type { LocationData } from './types/location_data';

export function getCsvLinesFromKioskList(params: {
  kioskList: Backbone.Collection<AbstractModel<KioskData>>;
  getLocationById: { (locationId: string): AbstractModel<LocationData> | undefined };
  shouldIncludeLocation: { (location: AbstractModel<LocationData>): boolean };
  shouldIncludeKiosk: { (kiosk: AbstractModel<KioskData>): boolean };
}): string[][] {
  const { kioskList, getLocationById, shouldIncludeLocation, shouldIncludeKiosk } = params;

  const lines: string[][] = [];
  lines.push([
    'Org',
    'Site',
    'Channel',
    'Time Zone',
    'Kiosk Name',
    'Last Seen',
    'MDM Provider',
    'Model',
    'Serial Number',
    'OS',
    'OS Version',
    'External IP Address',
    'Major App Version',
    'Printer Model',
    'Payment Model',
    'Scanner',
    'MSR',
    'HID',
    'Scale',
  ]);
  _.sortBy(kioskList.models, (kiosk) => {
    const locationId = getRequiredKioskValue(kiosk, 'locationId');
    const location = getLocationById(locationId);
    if (!location) {
      throw new Error(`Location ${locationId} not found for kiosk CSV export.`);
    }
    return `${location.get('orgNormalizedName')}-${location.get('normalizedName')}-${getRequiredKioskValue(kiosk, 'name').toLowerCase()}`;
  }).forEach((kiosk) => {
    const locationId = getRequiredKioskValue(kiosk, 'locationId');
    const location = getLocationById(locationId);
    if (!location) {
      throw new Error(`Location ${locationId} not found for kiosk CSV export.`);
    }
    if (!shouldIncludeLocation(location)) {
      return;
    }
    if (!shouldIncludeKiosk(kiosk)) {
      return;
    }
    const deviceType = getRequiredKioskValue(kiosk, 'deviceType');
    const status = kiosk.get('status');
    const timezone = getRequiredLocationValue(location, 'timezone');
    lines.push([
      getRequiredLocationValue(location, 'orgName'),
      getRequiredLocationValue(location, 'name'),
      getRequiredLocationValue(location, 'channelName'),
      timezone,
      getRequiredKioskValue(kiosk, 'name'),
      status?.lastStatusReceivedAt
        ? Time.moment(status.lastStatusReceivedAt).format('YYYY-MM-DD HH:mm')
        : 'Never',
      MdmProviderHelper.name(KioskDeviceTypeHelper.getMdmProvider(deviceType)),
      status
        ? KioskHelper.parseDeviceStatus(status.deviceStatus, deviceType).displayModelName ||
          KioskDeviceTypeHelper.name(deviceType)
        : KioskDeviceTypeHelper.name(deviceType),
      getRequiredKioskValue(kiosk, 'serialNumber'),
      status?.deviceStatus.os || KioskDeviceTypeHelper.toKioskOs(deviceType),
      status?.deviceStatus.osVersion || 'Unknown',
      status?.deviceStatus.externalIpAddress || '',
      KioskHelper.usesGarcon(kiosk.attributes as KioskData) ? '2' : '1',
      PrinterTypeHelper.name(getRequiredKioskValue(kiosk, 'printerType')),
      KioskPaymentTerminalModelHelper.name(getRequiredKioskValue(kiosk, 'paymentTerminalModel')),
      kiosk.get('hasScanner') ? 'Y' : 'N',
      kiosk.get('hasMsr') ? 'Y' : 'N',
      kiosk.get('hasHid') ? 'Y' : 'N',
      kiosk.get('hasScale') ? 'Y' : 'N',
    ]);
  });

  return lines;
}

function getRequiredKioskValue<FieldName extends keyof KioskData>(
  model: AbstractModel<KioskData>,
  fieldName: FieldName,
): NonNullable<KioskData[FieldName]> {
  const value = model.get(fieldName);
  if (value === undefined || value === null || value === '') {
    throw new Error(`Missing required ${fieldName} for kiosk CSV export.`);
  }
  return value;
}

function getRequiredLocationValue<FieldName extends keyof LocationData>(
  model: AbstractModel<LocationData>,
  fieldName: FieldName,
): NonNullable<LocationData[FieldName]> {
  const value = model.get(fieldName);
  if (value === undefined || value === null || value === '') {
    throw new Error(`Missing required ${fieldName} for kiosk CSV export.`);
  }
  return value;
}
